export type FuzzyBucket =
  | 'exact'
  | 'just_past'
  | 'past'
  | 'half_past'
  | 'quarter_past'
  | 'almost'
  | 'quarter_to'
  | 'to'

const NUMBER_WORDS: Record<number, string> = {
  0: 'twelve',
  1: 'one',
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six',
  7: 'seven',
  8: 'eight',
  9: 'nine',
  10: 'ten',
  11: 'eleven',
  12: 'twelve',
}

function hourWord12h(hour24: number): string {
  const h12 = (hour24 % 12) || 12
  return NUMBER_WORDS[h12]
}

function specialHourWord(hour24: number): 'midnight' | 'midday' | null {
  if (hour24 === 0) return 'midnight'
  if (hour24 === 12) return 'midday'
  return null
}

function resolveHourLabel(hour24: number): string {
  const special = specialHourWord(hour24)
  return special ?? hourWord12h(hour24)
}

export interface FuzzyTextOptions {
  preferJustGone?: boolean
}

export function getFuzzyText(date: Date, opts: FuzzyTextOptions = { preferJustGone: true }): string {
  const h24 = date.getHours()
  const m = date.getMinutes()

  if (m === 0) {
    const special = specialHourWord(h24)
    return special ? `It’s ${special}` : `It’s ${hourWord12h(h24)} o'clock`
  }

  const anchors = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]

  const nextH24 = (h24 + 1) % 24
  const currentLabel = resolveHourLabel(h24)
  const nextLabel = resolveHourLabel(nextH24)

  const findPreviousAnchor = () => {
    let previous = 0
    for (const anchor of anchors) {
      if (anchor <= m) {
        previous = anchor
      }
    }
    return previous
  }

  const anchorBefore = findPreviousAnchor()
  const idxBefore = anchors.indexOf(anchorBefore)
  const anchorAfter = anchors[(idxBefore + 1) % anchors.length]

  const deltaSince = m - anchorBefore
  const deltaUntil = (anchorAfter - m + 60) % 60

  const labelForAnchor = (anchor: number, context: 'current' | 'next'): string => {
    switch (anchor) {
      case 0: {
        const chosen = context === 'current' ? resolveHourLabel(h24) : resolveHourLabel(nextH24)
        if (chosen === 'midday' || chosen === 'midnight') {
          return `It’s ${chosen}`
        }
        return `It’s ${chosen} o'clock`
      }
      case 5:
        return `It’s five past ${currentLabel}`
      case 10:
        return `It’s ten past ${currentLabel}`
      case 15:
        return `It’s a quarter past ${currentLabel}`
      case 20:
        return `It’s twenty past ${currentLabel}`
      case 25:
        return `It’s twenty-five past ${currentLabel}`
      case 30:
        return `It’s half past ${currentLabel}`
      case 35:
        return `It’s twenty-five to ${nextLabel}`
      case 40:
        return `It’s twenty to ${nextLabel}`
      case 45:
        return `It’s a quarter to ${nextLabel}`
      case 50:
        return `It’s ten to ${nextLabel}`
      case 55:
        return `It’s five to ${nextLabel}`
      default:
        return anchor > 30 ? `It’s ${nextLabel} o'clock` : `It’s ${currentLabel} o'clock`
    }
  }

  const baseAnchorPrefersNext = deltaUntil < deltaSince
  const baseAnchor = baseAnchorPrefersNext ? anchorAfter : anchorBefore
  const baseContext: 'current' | 'next' = baseAnchor === 0 && baseAnchorPrefersNext ? 'next' : 'current'

  const withinJustGone = deltaSince >= 1 && deltaSince <= 2
  const withinAlmost = deltaUntil >= 0 && deltaUntil <= 2

  const basePhrase = labelForAnchor(baseAnchor, baseContext)

  if (withinJustGone && opts.preferJustGone) {
    const justGonePhrase = labelForAnchor(anchorBefore, 'current')
    return justGonePhrase.replace(/^It’s\s+/i, 'It’s just gone ')
  }

  if (withinAlmost) {
    const nextContext: 'current' | 'next' = anchorAfter === 0 ? 'next' : 'current'
    const nextPhrase = labelForAnchor(anchorAfter, nextContext)
    return nextPhrase.replace(/^It’s\s+/i, 'It’s almost ')
  }

  return basePhrase
}
